/**
 * WebSocket Types for ilovemacumba-app
 *
 * These types mirror the backend WebSocket types for type-safe communication.
 */

/**
 * WebSocket message types - must match backend WSMessageTypes
 */
export const WSMessageType = {
  // Connection lifecycle
  CONNECTED: "CONNECTED",
  DISCONNECTED: "DISCONNECTED",
  PING: "PING",
  PONG: "PONG",
  ERROR: "ERROR",

  // Request notifications
  REQUEST_CREATED: "REQUEST_CREATED",
  REQUEST_UPDATED: "REQUEST_UPDATED",
  REQUEST_APPROVED: "REQUEST_APPROVED",
  REQUEST_REJECTED: "REQUEST_REJECTED",
  REQUEST_CANCELLED: "REQUEST_CANCELLED",

  // Calendar realtime sync
  CALENDAR_EVENT_UPSERT: "calendar.event.upsert",
  CALENDAR_EVENT_DELETE: "calendar.event.delete",
  CALENDAR_ACTIVITY_UPSERT: "calendar.activity.upsert",
  CALENDAR_ACTIVITY_DELETE: "calendar.activity.delete",
  CALENDAR_RITUAL_BATH_UPSERT: "calendar.ritual_bath.upsert",
  CALENDAR_RITUAL_BATH_DELETE: "calendar.ritual_bath.delete",

  // Gamification realtime sync
  GAMIFICATION_POINTS_UPDATED: "gamification.points.updated",
  GAMIFICATION_BADGE_EARNED: "gamification.badge.earned",
  GAMIFICATION_STREAK_UPDATED: "gamification.streak.updated",
  GAMIFICATION_MILESTONE_REACHED: "gamification.milestone.reached",

  // General notification system
  NOTIFICATION_CREATED: "notification.created",

  // Social messaging (HTTP remains authoritative)
  SOCIAL_MESSAGE_CREATED: "social.message.created",
  SOCIAL_CONVERSATION_READ: "social.conversation.read",

  // Activity notifications (future)
  ACTIVITY_CREATED: "ACTIVITY_CREATED",
  ACTIVITY_UPDATED: "ACTIVITY_UPDATED",
  ACTIVITY_CANCELLED: "ACTIVITY_CANCELLED",

  // Event notifications (future)
  EVENT_CREATED: "EVENT_CREATED",
  EVENT_UPDATED: "EVENT_UPDATED",
  EVENT_CANCELLED: "EVENT_CANCELLED",

  // Medium notifications (future)
  MEDIUM_JOINED: "MEDIUM_JOINED",
  MEDIUM_LEFT: "MEDIUM_LEFT",
  MEDIUM_UPDATED: "MEDIUM_UPDATED",

  // Channel operations
  SUBSCRIBE: "SUBSCRIBE",
  UNSUBSCRIBE: "UNSUBSCRIBE",
  SUBSCRIBED: "SUBSCRIBED",
  UNSUBSCRIBED: "UNSUBSCRIBED",
} as const;

export type WSMessageType = (typeof WSMessageType)[keyof typeof WSMessageType];

/**
 * Base WebSocket message structure
 * Matches the API's WSMessage interface from ilovemacumba-api/src/types/websocket.ts
 */
export interface WSMessage<T = unknown> {
  type: WSMessageType;
  payload?: T;
  timestamp: string;
  channel?: string;
  /** Terreiro ID for scoped messages */
  terreiroId?: string;
  /** Unique message ID */
  messageId?: string;
}

export type CalendarEntityKind = "event" | "activity" | "ritual_bath";

export interface CalendarRealtimeScope {
  terreiroId?: string;
  mediumId?: string;
}

export interface CalendarRealtimeEntity {
  id: string;
  kind: CalendarEntityKind;
}

export interface CalendarSummaryPayload {
  id: string;
  title: string;
  date: string;
  time?: string;
  status?: string;
  type?: CalendarEntityKind;
}

export interface CalendarRealtimeEnvelope {
  type:
    | typeof WSMessageType.CALENDAR_EVENT_UPSERT
    | typeof WSMessageType.CALENDAR_EVENT_DELETE
    | typeof WSMessageType.CALENDAR_ACTIVITY_UPSERT
    | typeof WSMessageType.CALENDAR_ACTIVITY_DELETE
    | typeof WSMessageType.CALENDAR_RITUAL_BATH_UPSERT
    | typeof WSMessageType.CALENDAR_RITUAL_BATH_DELETE;
  scope: CalendarRealtimeScope;
  entity: CalendarRealtimeEntity;
  op: "upsert" | "delete";
  version: {
    updatedAt: string;
    seq?: number;
  };
  ts: string;
  correlationId: string;
  payload?: CalendarSummaryPayload;
}

export interface GamificationPointsUpdatedPayload {
  scope: {
    terreiroId: string;
    mediumId: string;
    userId?: string;
  };
  points: {
    totalPoints: number;
    monthlyPoints: number;
    weeklyPoints: number;
    currentStreak: number;
    longestStreak: number;
    lastActivityDate?: string;
    updatedAt: string;
  };
  transaction: {
    id: string;
    type: string;
    points: number;
    referenceType?: string;
    referenceId?: string;
    createdAt: string;
  };
  rank?: {
    scope: "TERREIRO";
    period: "ALL_TIME";
    previousPosition?: number;
    currentPosition?: number;
    delta?: number;
  };
  version: {
    updatedAt: string;
    seq?: number;
  };
  ts: string;
  correlationId: string;
}

export interface GamificationBadgeEarnedPayload {
  scope: {
    terreiroId: string;
    mediumId: string;
    userId?: string;
  };
  badge: {
    id: string;
    name: string;
    description: string;
    tier: string;
    type: string;
    pointsBonus: number;
    earnedAt: string;
  };
  ts: string;
  correlationId: string;
}

export interface GamificationStreakUpdatedPayload {
  scope: {
    terreiroId: string;
    mediumId: string;
    userId?: string;
  };
  streak: {
    previousCurrentStreak: number;
    currentStreak: number;
    previousLongestStreak: number;
    longestStreak: number;
    bonusAwarded: boolean;
    bonusType?: "WEEKLY" | "MONTHLY";
    updatedAt: string;
  };
  ts: string;
  correlationId: string;
}

export interface GamificationMilestoneReachedPayload {
  scope: {
    terreiroId: string;
    mediumId: string;
    userId?: string;
  };
  milestone: {
    points: number;
    label: string;
    previousTotalPoints: number;
    currentTotalPoints: number;
    reachedAt: string;
  };
  ts: string;
  correlationId: string;
}

/**
 * Request notification payloads
 * IMPORTANT: These must match the API's payload structure from ilovemacumba-api/src/types/websocket.ts
 */

/**
 * Nested request object in payloads
 */
export interface RequestPayloadData {
  id: string;
  type: string;
  status: string;
  title?: string;
  description?: string;
  reviewNotes?: string;
  reviewDate?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Nested medium object in payloads
 */
export interface MediumPayloadData {
  id: string;
  userId?: string;
  terreiroId?: string;
  status?: string;
  user?: {
    profile?: {
      firstName?: string;
      lastName?: string;
      avatarUrl?: string;
    };
  };
}

/**
 * REQUEST_CREATED payload - sent when a medium creates a new request
 * Received by: Terreiro admins
 */
export interface RequestCreatedPayload {
  request: RequestPayloadData;
  medium?: MediumPayloadData;
}

/**
 * REQUEST_UPDATED payload - sent when a request is updated
 * Received by: The user who created the request
 */
export interface RequestUpdatedPayload {
  request: RequestPayloadData;
  previousStatus?: string;
}

/**
 * REQUEST_APPROVED payload - sent when an admin approves a request
 * Received by: The user who created the request + terreiro members
 */
export interface RequestApprovedPayload {
  request: RequestPayloadData & { status: "APPROVED" };
  medium?: {
    id: string;
    status: string;
  };
}

/**
 * REQUEST_REJECTED payload - sent when an admin rejects a request
 * Received by: The user who created the request
 */
export interface RequestRejectedPayload {
  request: RequestPayloadData & { status: "REJECTED" };
  medium?: {
    id: string;
    status: string;
  };
}

/**
 * REQUEST_CANCELLED payload - sent when a pending request is cancelled
 * Received by: Terreiro admins
 */
export interface RequestCancelledPayload {
  request: RequestPayloadData & { status: "CANCELLED" };
}

/**
 * Connection payloads
 */
export interface ConnectedPayload {
  sessionId: string;
  userId: string;
  channels: string[];
}

export interface ErrorPayload {
  code: string;
  message: string;
}

/**
 * Channel subscription payloads
 */
export interface SubscribePayload {
  channel: string;
}

export interface SubscribedPayload {
  channel: string;
  success: boolean;
}

/**
 * WebSocket connection state
 */
export const WSConnectionState = {
  DISCONNECTED: "DISCONNECTED",
  CONNECTING: "CONNECTING",
  CONNECTED: "CONNECTED",
  RECONNECTING: "RECONNECTING",
  ERROR: "ERROR",
} as const;

export type WSConnectionState =
  (typeof WSConnectionState)[keyof typeof WSConnectionState];

/**
 * WebSocket service configuration
 */
export interface WSConfig {
  /** Base URL for the WebSocket server (defaults to API URL without /v1) */
  url?: string;
  /** Terreiro ID to subscribe to */
  terreiroId?: string;
  /** Medium ID for personal-profile scoped realtime */
  mediumId?: string;
  /** Authenticated user-level social messaging scope */
  scope?: "social";
  /** Auto-reconnect on disconnect (default: true) */
  autoReconnect?: boolean;
  /** Maximum reconnection attempts (default: 10) */
  maxReconnectAttempts?: number;
  /** Base delay between reconnection attempts in ms (default: 1000) */
  reconnectDelay?: number;
  /** Maximum reconnect delay in ms (default: 30000) */
  maxReconnectDelay?: number;
  /** Ping interval in ms (default: 25000) */
  pingInterval?: number;
  /** Pong timeout in ms (default: 10000) */
  pongTimeout?: number;
}

/**
 * Event listener callback type
 */
export type WSEventListener<T = unknown> = (message: WSMessage<T>) => void;

/**
 * WebSocket service interface
 */
export interface IWSService {
  /** Current connection state */
  state: WSConnectionState;
  /** Connect to the WebSocket server */
  connect(config?: WSConfig): void;
  /** Disconnect from the WebSocket server */
  disconnect(): void;
  /** Send a message to the server */
  send<T>(message: WSMessage<T>): boolean;
  /** Subscribe to a specific message type */
  on<T>(type: WSMessageType, listener: WSEventListener<T>): () => void;
  /** Unsubscribe from a specific message type */
  off<T>(type: WSMessageType, listener: WSEventListener<T>): void;
  /** Subscribe to all messages */
  onAny(listener: WSEventListener): () => void;
  /** Subscribe to all calendar realtime messages */
  onCalendar(listener: WSEventListener<CalendarRealtimeEnvelope>): () => void;
  /** Subscribe to gamification points update messages */
  onGamificationPointsUpdated(
    listener: WSEventListener<GamificationPointsUpdatedPayload>
  ): () => void;
  /** Check if connected */
  isConnected(): boolean;
}
